import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useApp } from "../context/AppContext";
import { useNotification } from "../context/NotificationContext";
import { api } from "../services/api";

const CreateProject = ({ project, onClose, isFirstProject, onUpdate }) => {
  const navigate = useNavigate();
  const { updateProjects, setCurrentProject, setCurrentProjectId, PROJECT_TYPE_ICONS, user } =
    useApp();
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    projectType: "",
    eta: "",
    visible_to_all_org_users: true,
    workflow_id: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const isEditing = project !== null && !isFirstProject;
  const isOwner = !isEditing || (user && project && project.user_id === user.user_id);

  useEffect(() => {
    loadWorkflows();
  }, []);

  useEffect(() => {
    if (project) {
      // Format the date to YYYY-MM-DD for the date input
      const formattedEta = project.eta ? new Date(project.eta).toISOString().split("T")[0] : "";

      setFormData({
        title: project.title,
        description: project.description,
        projectType: project.projectType,
        eta: formattedEta,
        visible_to_all_org_users:
          project.visible_to_all_org_users !== undefined ? project.visible_to_all_org_users : true,
        workflow_id: project.workflow_id || "",
      });
    }
  }, [project]);

  const loadWorkflows = async () => {
    try {
      setLoadingWorkflows(true);
      const response = await api.getWorkflows();
      // Backend returns { data: { workflows: [...] } }
      const workflowsData = response?.data?.workflows || [];
      setWorkflows(workflowsData);
    } catch (err) {
      console.error("Error loading workflows:", err);
      setWorkflows([]); // Ensure workflows is always an array
    } finally {
      setLoadingWorkflows(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let response;
      if (project) {
        // Update existing project
        if (onUpdate) {
          await onUpdate(formData);
          response = { id: project.id }; // Use existing project ID for response
        } else {
          response = await api.updateProject(project.id, formData);
        }
      } else {
        // Create new project
        response = await api.createProject(formData);
      }

      // Fetch updated projects list
      const projects = await api.getProjects();

      // Set the current project to the newly created/updated one
      const updatedProject = projects.find((p) => p.id === (response?.id || project.id));
      if (updatedProject) {
        setCurrentProject({
          id: updatedProject.id,
          name: updatedProject.title,
          type: updatedProject.projectType,
          status: updatedProject.status || "active",
        });
        setCurrentProjectId(updatedProject.id);
      }

      // Update projects state
      updateProjects(projects);

      if (onClose) {
        onClose();
      } else {
        // Navigate to the project dashboard if this is a new project
        navigate(`/project/${response.id}/dashboard`);
      }
    } catch (err) {
      const msg =
        err.message || `Failed to ${project ? "update" : "create"} project. Please try again.`;
      setError(msg);
      showNotification(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {isEditing
              ? "Edit Project"
              : isFirstProject
                ? "Create Your First Project"
                : "Create New Project"}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom align="center">
            {isEditing
              ? "Edit Project"
              : isFirstProject
                ? "Create Your First Project"
                : "Create New Project"}
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            {isEditing
              ? "Update your project details below."
              : isFirstProject
                ? "Get started by creating a new project to manage your certifications and compliance."
                : "Add a new project to manage your certifications and compliance."}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Project Title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  inputProps={{ maxLength: 50 }}
                  helperText={`${formData.title.length}/50`}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Project Type</InputLabel>
                  <Select
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    label="Project Type"
                    disabled={loading}
                  >
                    {Object.entries(PROJECT_TYPE_ICONS).map(
                      ([type, Icon]) =>
                        type !== "default" && (
                          <MenuItem key={type} value={type}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Icon sx={{ fontSize: "1.2rem" }} />
                              <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                            </Box>
                          </MenuItem>
                        )
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Estimated Completion Date"
                  name="eta"
                  type="date"
                  value={formData.eta}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Workflow (Optional)</InputLabel>
                  <Select
                    name="workflow_id"
                    value={formData.workflow_id}
                    onChange={handleChange}
                    label="Workflow (Optional)"
                    disabled={loading || loadingWorkflows}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {workflows.map((workflow) => (
                      <MenuItem key={workflow.id} value={workflow.id}>
                        {workflow.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {isOwner && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!formData.visible_to_all_org_users}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            visible_to_all_org_users: !e.target.checked,
                          }));
                        }}
                        name="is_private"
                        disabled={loading}
                      />
                    }
                    label="Make this project private"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Button type="submit" variant="contained" size="large" fullWidth disabled={loading}>
                  {loading
                    ? project
                      ? "Updating..."
                      : "Creating..."
                    : project
                      ? "Update Project"
                      : "Create Project"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </Box>
  );
};

export default CreateProject;
